import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * LiveTicker — warm ivory marquee.
 * 1. Track mounted exactly once; React never re-renders it.
 * 2. Polling mutates individual spans in place.
 * 3. In-place green/red flicker on value change; CSS marquee never resets.
 */
export default function LiveTicker() {
  const trackRef = useRef(null);
  const nodesRef = useRef({});
  const [built, setBuilt] = useState(false);
  const prevPrices = useRef({});

  const buildTrack = useCallback((items) => {
    const track = trackRef.current;
    if (!track || track.childElementCount > 0 || items.length === 0) return;
    nodesRef.current = {};
    const seq = [...items, ...items];
    seq.forEach((it, i) => {
      const row = document.createElement("div");
      row.className = "flex items-center gap-2 px-5 h-full border-r border-[#E2DDD3] whitespace-nowrap";
      row.setAttribute("data-testid", `ticker-item-${it.symbol}`);

      const sym = document.createElement("span");
      sym.className = "font-mono-fin text-xs tracking-wider text-[#3A3B3E] font-semibold";
      sym.textContent = it.symbol;
      row.appendChild(sym);

      const price = document.createElement("span");
      price.className = "font-mono-fin text-sm text-[#1A1A1A] tabular-nums rounded px-1 transition-colors";
      price.textContent = fmtNum(it.price);
      row.appendChild(price);

      const pct = document.createElement("span");
      const up = (it.change || 0) >= 0;
      pct.className = `font-mono-fin text-xs font-semibold ${up ? "text-[#16A34A]" : "text-[#DC2626]"}`;
      pct.textContent = `${up ? "▲" : "▼"} ${Math.abs(it.change_pct || 0).toFixed(2)}%`;
      row.appendChild(pct);

      track.appendChild(row);

      if (i < items.length) {
        nodesRef.current[it.symbol] = { price, pct, mirrorIndex: i + items.length };
      }
    });
    setBuilt(true);
  }, []);

  const applyUpdate = useCallback((items) => {
    const track = trackRef.current;
    if (!track) return;
    items.forEach((it) => {
      const slot = nodesRef.current[it.symbol];
      if (!slot) return;
      const prev = prevPrices.current[it.symbol];
      const changed = prev != null && prev !== it.price;
      slot.price.textContent = fmtNum(it.price);
      const up = (it.change || 0) >= 0;
      slot.pct.className = `font-mono-fin text-xs font-semibold ${up ? "text-[#16A34A]" : "text-[#DC2626]"}`;
      slot.pct.textContent = `${up ? "▲" : "▼"} ${Math.abs(it.change_pct || 0).toFixed(2)}%`;
      const mirror = track.children[slot.mirrorIndex];
      if (mirror) {
        mirror.children[1].textContent = slot.price.textContent;
        mirror.children[2].textContent = slot.pct.textContent;
        mirror.children[2].className = slot.pct.className;
      }
      prevPrices.current[it.symbol] = it.price;
      if (changed) {
        const cls = it.price > prev ? "flicker-up" : "flicker-down";
        slot.price.classList.remove("flicker-up", "flicker-down");
        // eslint-disable-next-line no-unused-expressions
        slot.price.offsetWidth;
        slot.price.classList.add(cls);
        if (mirror) {
          mirror.children[1].classList.remove("flicker-up", "flicker-down");
          // eslint-disable-next-line no-unused-expressions
          mirror.children[1].offsetWidth;
          mirror.children[1].classList.add(cls);
        }
      }
    });
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const fetchOnce = async () => {
      try {
        const r = await api.get("/market/ticker");
        if (!alive) return;
        const items = r.data?.data || [];
        if (items.length === 0) return;
        if (!built) buildTrack(items);
        else applyUpdate(items);
      } catch (_) { /* silent */ }
      finally {
        if (alive) timer = setTimeout(fetchOnce, 25_000);
      }
    };
    fetchOnce();
    return () => { alive = false; clearTimeout(timer); };
  }, [built, buildTrack, applyUpdate]);

  return (
    <div className="relative overflow-hidden h-9 border-b border-[#E2DDD3]" style={{ background: "#F7F4EE" }} data-testid="live-stock-ticker-tape">
      <div className="absolute left-0 top-0 h-full flex items-center bg-[#D91E2A] px-3 z-10">
        <span className="pulse-dot inline-block w-1.5 h-1.5 rounded-full bg-white mr-2" />
        <span className="text-[10px] font-mono-fin uppercase tracking-widest text-white font-bold">Live</span>
      </div>
      <div className="h-full overflow-hidden ml-16">
        <div ref={trackRef} className="marquee-track h-full items-center" />
      </div>
    </div>
  );
}

function fmtNum(v) {
  if (v == null) return "—";
  try { return v.toLocaleString("en-IN", { maximumFractionDigits: 2 }); } catch { return String(v); }
}
